<template>
  <v-container fluid grid-list-xs>
    <v-layout row wrap>
      <v-flex xs12>
        <h1 class="title">
          {{ title }}
        </h1>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-3">
        <v-autocomplete
          v-model="selectedAddress"
          :items="addresses"
          item-value="id"
          :item-text="addressName"
          :label="$tc('models.saved_address', 2)"
          @change="fillAddress"
          :search-input.sync="addressSearch"
          browser-autocomplete="hydra-new-password"
          :loading="loading"
          :hint="$t('addressSearchHint')"
          persistent-hint
          return-object
        ></v-autocomplete>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <div class="v-input upper v-input--is-label-active v-input--is-dirty theme--light v-text-field v-text-field--is-booted">
          <input type="text" :placeholder="$t('attrs.search_location')" name="address_auto" :class="`${ addressType }-autocomplete auto-border`">
        </div>
      </v-flex>

      <v-flex xs12 class="pt-0 pb-0">
        <v-text-field
          v-model="address.name"
          class="pt-0 pb-0"
          name="name"
          :label="$t('attrs.name')"
          v-validate="'required|max:35'"
          :error-messages="errors.collect('name')"
          data-vv-name="name"
          :data-vv-as="$t('attrs.name')"
          required
        ></v-text-field>
      </v-flex>

      <v-flex xs12 md4 class="pt-0 pb-0">
        <v-text-field
          v-model="address.email"
          name="email"
          :label="$t('attrs.email')"
          v-validate="'required|email'"
          :error-messages="errors.collect('email')"
          data-vv-name="email"
          :data-vv-as="$t('attrs.email')"
          :disabled="disableEmail"
          required
        ></v-text-field>
      </v-flex>

      <v-flex xs12 md8 class="pt-0 pb-0">
        <vue-phone-number-input
          v-model="phone"
          name="phone"
          :no-example="true"
          :error-color="'#FF5252'"
          :valid-color="'#476AFE'"
          :default-country-code="defaultCountryCode"
          @update="getPayload"
          v-validate="`required|destPhoneValidation:${address.phone}`"
          :error-messages="errors.collect('phone')"
          data-vv-name="phone"
          :data-vv-as="$t('attrs.phone')"
          :disabled="disablePhone"
          :preferred-countries="['MX', 'US']"
        ></vue-phone-number-input>

        <div class="v-text-field__details">
          <div class="v-messages theme--light error--text">
            <div class="v-messages__wrapper">
              <div class="v-messages__message message-transition-enter-to" style="">
                {{ errors.first('phone') }}
              </div>
            </div>
          </div>
        </div>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-text-field
          v-model="address.company"
          name="company"
          :label="$t('attrs.company')"
          v-validate="'required'"
          :error-messages="errors.collect('company')"
          data-vv-name="company"
          :data-vv-as="$t('attrs.company')"
          required
        ></v-text-field>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-text-field
          v-model="address.rfc"
          name="rfc"
          :label="$t('attrs.rfc')"
        ></v-text-field>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-autocomplete
          v-model="address.country_id"
          :items="countries"
          item-value="id"
          item-text="name"
          :label="$tc('models.country')"
          @change="fetchStates"
          v-validate="'required'"
          :error-messages="errors.collect('country_id')"
          data-vv-name="country_id"
          :data-vv-as="$tc('models.country')"
          browser-autocomplete="hydra-new-password"
          required
          :disabled="loading"
        ></v-autocomplete>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-text-field
          v-model="address.postal_code"
          name="postal_code"
          :label="$t('attrs.postal_code')"
          v-validate="'required|max:7'"
          :error-messages="errors.collect('postal_code')"
          data-vv-name="postal_code"
          :data-vv-as="$t('attrs.postal_code')"
          @change="fetchColonies"
          required
          :disabled="loading"
        ></v-text-field>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-combobox
          v-model="address.state"
          :items="states"
          item-value="name"
          item-text="name"
          :return-object="false"
          :label="$tc('models.state')"
          v-validate="'required'"
          :error-messages="errors.collect('state')"
          data-vv-name="state"
          :data-vv-as="$t('attrs.state')"
          required
          :disabled="loading"
        ></v-combobox>
        <!-- :disabled="!address.country_id" -->
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-text-field
          v-model="address.city"
          name="city"
          :label="$t('attrs.city')"
          v-validate="'required|max:35'"
          :error-messages="errors.collect('city')"
          data-vv-name="city"
          :data-vv-as="$t('attrs.city')"
          required
        ></v-text-field>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-combobox
          v-model="address.neighborhood"
          :items="colonies"
          item-value="name"
          item-text="name"
          :return-object="false"
          :label="$tc('attrs.neighborhood')"
          v-validate="'required'"
          :error-messages="errors.collect('neighborhood')"
          data-vv-name="neighborhood"
          :data-vv-as="$t('attrs.neighborhood')"
          required
          :disabled="loading"
          v-if="isMexico"
        ></v-combobox>

        <v-text-field
          v-model="address.neighborhood"
          item-value="name"
          item-text="name"
          v-validate="'required'"
          :label="$tc('attrs.neighborhood')"
          :error-messages="errors.collect('neighborhood')"
          data-vv-name="neighborhood"
          :data-vv-as="$t('attrs.neighborhood')"
          required
          v-else
        ></v-text-field>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-text-field
          v-model="address.street"
          name="street"
          :label="$t('attrs.street')"
          v-validate="'required|max:30'"
          :error-messages="errors.collect('street')"
          data-vv-name="street"
          :data-vv-as="$t('attrs.street')"
          required
        ></v-text-field>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-text-field
          v-model="address.number"
          name="number"
          :label="$t('attrs.number')"
          v-validate="'required'"
          :error-messages="errors.collect('number')"
          data-vv-name="number"
          :data-vv-as="$t('attrs.number')"
          required
        ></v-text-field>
      </v-flex>

      <v-flex xs12 md6 class="pt-0 pb-0">
        <v-text-field v-model="address.reference"
          name="reference"
          :label="`${$t('attrs.reference')} (${$t('optional')})`"
          v-validate="'max:35'"
          maxlength="135"
          placeholder="No mayor a 35 caracteres"
          :error-messages="errors.collect('reference')"
          data-vv-name="reference"
          :data-vv-as="$t('attrs.reference')"
        ></v-text-field>
      </v-flex>

      <v-flex xs12 class="pt-0 pb-0">
        <v-checkbox v-model="address.save" :label="$t('saveAddress')"></v-checkbox>
      </v-flex>

    </v-layout>
  </v-container>
</template>

<script>
import axios from 'axios'
import State from '@/client/state'
import Address from '@/client/address'

import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';

export default {
  name: 'ShipmentNewAddressComp',

  props: {
    title: String,
    addressType: {
      type: String,
      required: true
    },
    countries: {
      type: Array,
      defaul: () => []
    },
    showDestError: {
      type: Boolean,
      default: false
    },
    settings: {
      type: Array,
      defaul: () => []
    },
    disableEmail: {
      type: Boolean,
      default: false
    },
    disablePhone: {
      type: Boolean,
      default: false
    },
    defaultRFC: {
      type: String
    },
  },

  data () {
    return {
      loading: false,
      addressSearch: '',
      addresses: [],
      selectedAddress: {},
      address: {},
      states: [],
      colonies: [],
      codeAddress: '',
      country: '',
      subLocalities: [],
      defaultCountryCode: 'MX',
      place: [],
      selectedCountry: {},
      phone: '',
    }
  },

  components: {
    VuePhoneNumberInput
  },

  computed: {
    selectedCountryName () {
      return this.selectedCountry.name
    },

    isMexico () {
      return this.selectedCountry.code === 'MX'
    },
  },

  methods: {
    async fetchStates () {
      this.states = []
      this.selectedCountry = this.countries.find(c => c.id === this.address.country_id) || {}
      if (!this.address.country_id) {
        var countryName = this.setGeocodes(this.place, 'country')
        var country = this.countries.find( c => c.name.toLowerCase() === countryName.toLowerCase() )

        if (country) {
          this.address.country_id = country.id
          this.address.country = country
        }
      }

      this.states = await State.all({
        where: { country_id: this.address.country_id },
        order: 'name ASC',
        per_page: 2000
      })
    },

    async fetchColonies () {
      if (!this.isMexico) { return }
      if (!this.address.postal_code) { return }

      this.loading = true
      try {
        const url = 'https://sat.api.hydraship.app/api/colonies'
        const res = await axios.get(url, { params: { postal_code: this.address.postal_code } })
        this.colonies = res.data.data
        if (this.colonies.length) {
          this.address.city = this.colonies[0].city
          this.address.state = this.colonies[0].state
        }
      } catch (e) {
        this.handleErr(e)
      }
      this.loading = false
    },

    async fillAddress () {
      this.address = { ...this.selectedAddress }
      this.phone = this.address.phone
      this.address.visible = false
      this.fetchStates()
      this.selectedAddress = null

      //Google's geocoding API - implemented to check whether the address is correct or not.
      // this.getGeoCodes ()
    },

    addressFilter (itemTitle, queryText, item) {
      const name = item.raw.name.toLowerCase()
      const aka = item.raw.aka.toLowerCase()
      const company = item.raw.company.toLowerCase()
      const searchText = queryText.toLowerCase()

      return name.indexOf(searchText) > -1 ||
        aka.indexOf(searchText) > -1 ||
        company.indexOf(searchText) > -1
    },

    addressName (address) {
      let name = ''
      if (address.aka) {
        name += `${address.aka} | `
      }
      name += address.name

      return name
    },

    testAddress () {
      this.address.name = 'test1'
      this.address.email = 'test@test.com'
      this.address.phone = '1231231231'
      this.address.company = 'test'
      this.address.country_id = 1
      this.address.state = 'Nuevo León'
      this.address.street = 'test'
      this.address.neighborhood = 'test'
      this.address.city = 'test'
      this.address.postal_code = 64348
    },

    async setCityState() {
      try {
        this.clearCityState()

        for (var _i = 0, _a = this.place.address_components; _i < _a.length; _i++) {
          var component = _a[_i];
          var componentType = component.types[0];
          switch (componentType) {
            case "street_number": {
              this.address.street = (component.long_name || component.short_name || '');
              break;
            }
            case "route": {
              if (this.address.street) {
                this.address.street += (' ' + (component.long_name || component.short_name || ''));
              } else {
                this.address.street = (component.long_name || component.short_name || '');
              }
              break;
            }
            case "postal_code": {
              this.address.postal_code = (component.long_name || component.short_name || '');
              break;
            }
            case "postal_code_suffix": {
              // postcode = "".concat(postcode, "-").concat(component.long_name);
              break;
            }
            case "sublocality_level_1": {
              this.address.neighborhood = (component.long_name || component.short_name || '')
              break
            }
            case "sublocality": {
              if ((this.address.neighborhood) && (this.address.neighborhood !== component.long_name)) {
                this.address.neighborhood += (' ' + (component.long_name || component.short_name || ''));
              } else {
                this.address.neighborhood = (component.long_name || component.short_name || '');
              }
              break;
            }
            case "country": {
              var country = this.countries.find( country => country.name.toLowerCase() === component.long_name.toLowerCase() )
              if (country) this.address.country_id = country.id
              break;
            }
            case "administrative_area_level_1": {
              if (this.states.length == 0) await this.fetchStates()
              const state = this.states.find( state => state.name.spanishize().toLowerCase() === component.long_name.spanishize().toLowerCase() )

              this.address.state = state ? state.name : component.long_name.spanishize()
              break;
            }
            case "administrative_area_level_2": {
              this.address.city = (component.long_name || component.short_name || '')
              break;
            }
          }
        }

        if (this.place.name) this.address.company = this.place.name
        this.$forceUpdate()
      } catch (err) {
         // console.log('error', err)
      }
    },

    setGeocodes (res, nameType) {
      var obj = res.address_components.find( comp => comp.types.includes(nameType) )

      if (obj && obj.long_name) {
        return obj.long_name.toLowerCase()
      } else {
        return ''
      }
    },

    clearCityState () {
      this.address.city = ''
      this.address.state = ''
      this.address.neighborhood = ''
      this.address.company = ''
      this.address.country = ''
    },

    getPayload(payload) {
      if (payload.countryCallingCode) this.address.calling_code = payload.countryCallingCode
      this.address.phone = payload.nationalNumber
    },

    async fetchAddresses() {
      try {
        this.addresses = await Address.all({
          order: 'created_at desc',
          per_page: 10
        })
      } catch (e) {
        this.handleErr(e)
      }
    },
  },

  watch: {
    async addressSearch () {
      if (this.loading) { return }

      if (this.addressSearch && this.addressSearch.length > 2) {
        this.loading = true
        try {
          this.addresses = await Address.all({
            search: this.addressSearch
          })
        } catch (e) {
          // console.log(e)
        }
        this.loading = false
      } else {
        this.addresses = []
      }
    },

    'address.reference' (val) {
      if (val.length > 35) {
        alert('La referencia no puede tener más de 35 caracteres')
        this.address.reference = val.slice(0, 35)
      }
    },

    address: {
      handler () {
        if (this.disableEmail) {
          const email = this.settings.find(o => o.key === `${this.addressType}_email`) || {}
          this.address.email = email.value
        }

        if (this.disablePhone) {
          const phone = this.settings.find(o => o.key === `${this.addressType}_phone_number`) || {}
          this.address.phone = phone.value
        }
      },
      deep: true
    },

    countries () {
      const country = this.countries.find(o => o.code === 'MX')
      if (!this.address.country_id && country) {
        this.address.country_id = country.id
        this.address.country_name = country.name
        this.address.country_code = country.code
        this.address.country = country
        this.selectedCountry = country
        this.fetchStates()
      }
    },

    defaultRFC () {
      if (!this.address.rfc && this.defaultRFC) {
        this.address.rfc = this.defaultRFC
      }
    }
  },

  async mounted () {
    this.fetchAddresses()

    const newShipment = JSON.parse(window.sessionStorage.getItem('new_shipment') || '{}')
    if (this.addressType === 'origin') {
      this.address.postal_code = newShipment.origin_postal_code
    } else if (this.addressType === 'destination') {
      this.address.postal_code = newShipment.destination_postal_code
    }

    const country = this.countries.find(o => o.code === 'MX')
    if (!this.address.country_id && country) {
      this.address.country_id = country.id
      this.address.country_name = country.name
      this.address.country_code = country.code
      this.address.country = country
      this.fetchStates()
    }

    if (!this.address.rfc && this.defaultRFC) {
      this.address.rfc = this.defaultRFC
    }
  }
}
</script>

<style type="text/css" scoped>
  .auto-border { border-bottom: 1px solid grey }
</style>
